import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import {
  Box,
  Button,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Switch,
  FormControlLabel,
  Tooltip,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  ListSubheader,
  InputAdornment,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  Chat as ChatIcon,
  Psychology as AgentIcon,
  Group as GroupIcon,
  Store as StoreIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Build as BuildIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Speed as PerformanceIcon,
  Storage as DataIcon,
  DarkMode as DarkModeIcon,
  LocalHospital as MedicalIcon,
  Person as PatientIcon,
  Event as AppointmentIcon,
  LightMode as LightModeIcon,
  AccountCircle as AccountIcon,
  AccountCircle as AccountCircleIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Publish as PublishIcon,
  Home as HomeIcon,
  Description as FormIcon,
  PlayArrow as RuntimeIcon,
  Code as CodeIcon,
  Create as WriteIcon,
  Science as ResearchIcon,
  Assistant as AssistantIcon,
  ViewModule as TemplateIcon,
  Api as ApiIcon,
  Link as WebhookIcon,
  Extension as ExtensionIcon,
  Key as KeyIcon,
  Storefront as MarketplaceIcon,
  AccountTree as FlowIcon,
  Timeline as TimelineIcon,
  Memory as MemoryIcon,
  Computer as ComputerIcon,
  CloudQueue as CloudIcon,
  BusinessCenter as BusinessIcon,
  Monitor as MonitorIcon,
  Category as CategoryIcon,
  Layers as LayersIcon,
  TableChart as SchemaIcon,
  Widgets as WidgetsIcon,
  AutoAwesome as AutoIcon,
  SmartToy as BotIcon,
  Hub as HubIcon,
  Groups as TeamIcon,
  People as PeopleIcon,
  Folder as FolderIcon,
  Work as WorkIcon,
  School as EducationIcon,
  Business as CompanyIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Star as StarIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  MoreVert as MoreIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  GridView as GridIcon,
  List as ListIcon,
  Apps as AppsIcon,
  Tune as TuneIcon,
  Refresh as RefreshIcon,
  GetApp as DownloadIcon,
  CloudUpload as UploadIcon,
  FileCopy as CopyIcon,
  Launch as LaunchIcon,
  OpenInNew as OpenIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  AutoAwesome as AutoAwesomeIcon,
  TrendingUp as TrendingUpIcon,
  Campaign as CampaignIcon,
  CheckCircle as CheckCircleIcon,
  Map as MapIcon,
  PushPinOutlined as PushPinOutlinedIcon,
  PushPin as PushPinIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import GlobalSearch from './GlobalSearch';
import FullscreenButton from '../FullscreenButton';
import { useRoleContext } from '../../contexts/RoleContext';
import BrandLogo from '../BrandLogo';
import { getBrand } from '../../config/brand.js';
import RoleIndicator from '../RoleIndicator';
import NotificationsPage from '../Notifications/NotificationsPage'; // Add this import
import { listStudioRecents, toggleStudioRecentPin } from '../../utils/studioRecents';
import { STUDIO_HELP_SECTION_BY_ID } from '../../pages/studio/studioHelpCatalog';

const DRAWER_WIDTH = 280;

/** Flat nav targets for InterviewFlow command palette (single + expandable/group children). */
function flattenFilteredNavigationForPalette(items) {
  const routes = [];
  (items || []).forEach((item) => {
    if (item.type === 'single' && item.path) {
      routes.push({ label: item.label, path: item.path, section: 'Navigation' });
    }
    if ((item.type === 'expandable' || item.type === 'group') && item.children?.length) {
      item.children.forEach((ch) => {
        if (ch.path) {
          routes.push({
            label: `${item.label} — ${ch.label}`,
            path: ch.path,
            section: item.label,
          });
        }
      });
    }
  });
  return routes;
}

function navPathSelected(pathname, navPath) {
  if (!navPath) return false;
  if (pathname === navPath) return true;
  if (navPath === '/studio' || navPath === '/home') return false;
  return pathname.startsWith(`${navPath}/`);
}

// Global flag to prevent multiple simultaneous auth calls
let isNavAuthCallInProgress = false;

// Utility: Try both session (cookie) and JWT auth for user info
async function fetchUserWithAuthFallback() {
  // Quick check - if no token and no user data, don't even try
  const token = localStorage.getItem('tkn');
  const userData = localStorage.getItem('user');

  // If no token and no user data, user is definitely not authenticated
  if (!token && !userData) {
    console.log('No authentication data found, skipping API call');
    return null;
  }

  // If we have user data but no token, the session might be expired
  if (!token && userData) {
    console.log('User data exists but no token, session may be expired');
    // Clear expired user data
    localStorage.removeItem('user');
    return null;
  }

  // Prevent multiple simultaneous calls
  if (isNavAuthCallInProgress) {
    console.log('Nav auth call already in progress, skipping');
    return null;
  }

  isNavAuthCallInProgress = true;

  try {
    // Try JWT auth first (since we're using JWT from Python backend)
    if (token) {
      const res = await fetch('/api/v1/users/current_user', {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        cache: 'no-store',
      });

      if (res.ok) {
        const data = await res.json();
        return data;
      }
    }

    // Fallback: Try session-based auth
    const res = await fetch('/api/v1/users/current_user', {
      credentials: 'include',
      cache: 'no-store',
    });

    if (res.ok) {
      const data = await res.json();
      return data;
    }

    // If we get here, user is not authenticated - this is normal, don't log
    return null;
  } catch (error) {
    // Only log actual errors, not 401 responses
    if (error.name !== 'TypeError') {
      console.log('Error fetching user info:', error);
    }
    return null;
  } finally {
    isNavAuthCallInProgress = false;
  }
}

function AppNavigation({
  children,
  title = 'AI Studio',
  darkMode,
  onToggleTheme,
  onLogout,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasPermission, userRole } = useRoleContext();
  const [lastFetchTime, setLastFetchTime] = useState(0);

  // Force reload user role from server on mount to avoid stale localStorage/cache
  useEffect(() => {
    // Only fetch if user has a token or is likely authenticated
    const token = localStorage.getItem('tkn');
    const user = localStorage.getItem('user');

    // Check if we're on a page that requires authentication
    const currentPath = window.location.pathname;
    const publicPages = ['/', '/signin', '/signup', '/landing', '/about', '/apps', '/v2/demo', '/v2/p'];
    const isPublicPage = publicPages.some((page) => currentPath.startsWith(page));

    // Debounce API calls - don't fetch more than once every 5 seconds
    const now = Date.now();

    // Determine if we should make an API call
    const shouldMakeApiCall = (token || user)
                              && !(isPublicPage && !token && !user)
                              && (now - lastFetchTime >= 5000);

    if (shouldMakeApiCall) {
      setLastFetchTime(now);
      fetchUserWithAuthFallback()
        .then((data) => {
          // Only reload if userRole is defined and different
          if (
            data?.user?.role
            && userRole
            && data.user.role !== userRole
          ) {
            window.location.reload();
          }
        })
        .catch(() => { /* ignore errors */ });
    }
  }, [userRole, location.pathname, lastFetchTime]);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [isNavCollapsed, setIsNavCollapsed] = useState(() => {
    // Persist navigation state in localStorage
    const saved = localStorage.getItem('navCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  // Only use expandedItems and setExpandedItems
  //
  // Initialize expandedItems from localStorage if present
  const [expandedItems, setExpandedItems] = useState(() => {
    const saved = localStorage.getItem('expandedMenuItems');
    return saved ? JSON.parse(saved) : {};
  });
  const [notifications, setNotifications] = useState([]);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [open, setOpen] = useState(() => !isMobile && !isNavCollapsed);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingTenantsCount, setPendingTenantsCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState('');
  const [recentsRefreshKey, setRecentsRefreshKey] = useState(0);

  const bumpRecents = useCallback(() => {
    setRecentsRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    bumpRecents();
  }, [location.pathname, bumpRecents]);

  // Save navigation state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('navCollapsed', JSON.stringify(isNavCollapsed));
  }, [isNavCollapsed]);

  // Save expanded items state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('expandedMenuItems', JSON.stringify(expandedItems));
  }, [expandedItems]);

  useEffect(() => {
    if (location.pathname.startsWith('/studio/help')) {
      setExpandedItems((prev) => ({ ...prev, 'help-support': true }));
    }
  }, [location.pathname]);

  // Example notifications (replace with real API in production)
  useEffect(() => {
    setNotifications([
      {
        id: 1,
        title: 'Document Uploaded',
        message: 'Your file "report.pdf" was successfully uploaded.',
        type: 'success',
        time: '2 min ago',
      },
      {
        id: 2,
        title: 'Agent Flow Completed',
        message: 'Agentic flow "Customer Support" finished execution.',
        type: 'info',
        time: '10 min ago',
      },
      {
        id: 3,
        title: 'API Error',
        message: 'Failed to connect to OpenAI API.',
        type: 'error',
        time: '1 hour ago',
      },
    ]);
  }, []);

  useEffect(() => {
    if (getBrand() !== 'interviewflow') return undefined;
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || (e.key !== 'k' && e.key !== 'K')) return;
      const t = e.target;
      if (t) {
        if (typeof t.closest === 'function' && t.closest('input, textarea, select')) return;
        if (t.isContentEditable) return;
      }
      e.preventDefault();
      setCommandQuery('');
      setCommandOpen((openCmd) => !openCmd);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Fetch pending tenants count for super admin
  /* useEffect(() => {
    const fetchPendingTenantsCount = async () => {
      if (userRole === 'super_admin') {
        try {
          const response = await fetch('/api/v1/onboarding/tenants/pending', {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('tkn')}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            setPendingTenantsCount(data.tenants?.length || 0);
          }
        } catch (error) {
          console.error('Error fetching pending tenants count:', error);
        }
      }
    };

    fetchPendingTenantsCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingTenantsCount, 30000);
    return () => clearInterval(interval);
  }, [userRole]); */

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavToggle = () => {
    const newCollapsedState = !isNavCollapsed;
    setIsNavCollapsed(newCollapsedState);
    setOpen(!newCollapsedState);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  // Only one handleExpandClick function:
  const handleExpandClick = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Add missing notification handlers
  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    if (onLogout) {
      onLogout();
    }
  };

  const drawerWidth = isNavCollapsed ? 70 : 240;

  // Fix mobile responsiveness for drawer
  const mobileDrawerWidth = 280; // Wider for mobile to show full text
  const desktopDrawerWidth = isNavCollapsed ? 70 : 240;

  const isInterviewflow = getBrand() === 'interviewflow';

  /** Automation submenu entries hidden on InterviewFlow: duplicate MCP shortcut, Baiglabs vertical demos. */
  /** MCP Dashboard stays under System/MCP; permit tools are restored as first-class on IF. */
  const INTERVIEWFLOW_HIDDEN_AUTOMATION_IDS = ['mcp-dashboard'];

  const automationWorkflowChildrenBase = [
    {
      id: 'studio', label: 'Studio', icon: HubIcon, path: '/studio', permission: 'agents',
    },
    {
      id: 'studio-flows', label: 'Flows', icon: FlowIcon, path: '/studio/flows', permission: 'agents',
    },
    {
      id: 'studio-apps', label: 'Apps', icon: AppsIcon, path: '/studio/apps', permission: 'agents',
    },
    {
      id: 'studio-templates', label: 'Starters', icon: StarIcon, path: '/studio/templates', permission: 'agents',
    },
    {
      id: 'studio-extensions', label: 'Extensions', icon: ExtensionIcon, path: '/studio/extensions', permission: 'agents',
    },
    {
      id: 'studio-connect', label: 'AI assistants', icon: KeyIcon, path: '/studio/connect', permission: 'agents',
    },
    {
      id: 'workflow-templates',
      label: 'Workflow Templates',
      icon: TemplateIcon,
      path: '/workflow-templates',
      permission: 'agents',
    },
    {
      id: 'scheduled-flows',
      label: 'Scheduled Flows',
      icon: ScheduleIcon,
      path: '/scheduled-flows',
      permission: 'agents',
    },
    {
      id: 'flow-execution-tracker',
      label: 'Execution Tracker',
      icon: TimelineIcon,
      path: '/flow-execution-tracker',
      permission: 'agents',
    },
    {
      id: 'automation-rules',
      label: 'Automation Rules',
      icon: SettingsIcon,
      path: '/automation-rules',
      permission: 'settings',
    },
    {
      id: 'mcp-dashboard',
      label: 'MCP Dashboard',
      icon: CodeIcon,
      path: '/mcp-dashboard',
      permission: 'agents',
    },
    {
      id: 'automation-dashboard',
      label: 'Automation Dashboard',
      icon: AutoAwesomeIcon,
      path: '/automation',
      permission: 'dashboard',
    },
    {
      id: 'permit-analytics',
      label: 'Permit Analytics',
      icon: AnalyticsIcon,
      path: '/permit-analytics',
      permission: 'analytics',
    },
    {
      id: 'zoning-check',
      label: 'Zoning Check',
      icon: MapIcon,
      path: '/zoning-check',
      permission: 'agents',
    },
  ];

  const automationWorkflowChildren = isInterviewflow
    ? automationWorkflowChildrenBase.filter(
      (item) => item?.id && !INTERVIEWFLOW_HIDDEN_AUTOMATION_IDS.includes(item.id),
    )
    : automationWorkflowChildrenBase;

  const navigationItems = [
    {
      id: 'dashboard',
      label: isInterviewflow ? 'Home' : 'Dashboard',
      icon: DashboardIcon,
      path: '/dashboard',
      type: 'single',
      permission: 'dashboard',
    },
    {
      id: 'chat',
      label: 'Chat',
      icon: ChatIcon,
      path: '/home',
      type: 'single',
      permission: 'dashboard', // Everyone with dashboard access can chat
    },
    {
      id: 'chat-widget-demo',
      label: 'Chat Widget Demo',
      icon: WidgetsIcon,
      path: '/chat-widget-demo',
      type: 'single',
      permission: 'dashboard', // Everyone with dashboard access can view demo
    },
    {
      id: 'medical',
      label: 'Medical',
      icon: MedicalIcon,
      type: 'expandable',
      permission: 'dashboard',
      children: [
        {
          id: 'medical-patients',
          label: 'Patients',
          icon: PatientIcon,
          path: '/medical/patients',
          permission: 'dashboard',
        },
        {
          id: 'medical-appointments',
          label: 'Appointments',
          icon: AppointmentIcon,
          path: '/medical/appointments',
          permission: 'dashboard',
        },
      ],
    },
    {
      id: 'crm',
      label: 'CRM',
      icon: BusinessIcon,
      type: 'expandable',
      permission: 'dashboard',
      children: [
        {
          id: 'crm-dashboard',
          label: 'CRM Dashboard',
          icon: DashboardIcon,
          path: '/crm',
          permission: 'dashboard',
        },
        {
          id: 'crm-contacts',
          label: 'Contacts / Leads',
          icon: PeopleIcon,
          path: '/crm/contacts',
          permission: 'dashboard',
        },
        {
          id: 'crm-properties',
          label: 'Properties',
          icon: HomeIcon,
          path: '/crm/properties',
          permission: 'dashboard',
        },
        {
          id: 'crm-opportunities',
          label: 'Opportunities',
          icon: TrendingUpIcon,
          path: '/crm/opportunities',
          permission: 'dashboard',
        },
        {
          id: 'crm-tasks',
          label: 'Tasks / Reminders',
          icon: ScheduleIcon,
          path: '/crm/tasks',
          permission: 'dashboard',
        },
        {
          id: 'crm-notes',
          label: 'Notes / Logs',
          icon: DescriptionIcon,
          path: '/crm/notes',
          permission: 'dashboard',
        },
        {
          id: 'crm-lead-stages',
          label: 'Lead Stages',
          icon: TimelineIcon,
          path: '/crm/lead-stages',
          permission: 'dashboard',
        },
        {
          id: 'crm-notifications',
          label: 'Notifications',
          icon: NotificationsIcon,
          path: '/crm/notifications',
          permission: 'dashboard',
        },
        {
          id: 'crm-ai-fields',
          label: 'AI Fields',
          icon: AutoAwesomeIcon,
          path: '/crm/ai-fields',
          permission: 'dashboard',
        },
        {
          id: 'crm-campaigns',
          label: 'Campaigns',
          icon: CampaignIcon,
          path: '/crm/campaigns',
          permission: 'dashboard',
        },
      ],
    },
    {
      id: 'forms',
      label: 'Forms',
      icon: FormIcon,
      type: 'expandable',
      permission: 'forms',
      children: [
        {
          id: 'form-builder',
          label: 'Form Builder',
          icon: BuildIcon,
          path: '/formBuilder',
          permission: 'forms',
        },
        {
          id: 'draft-forms',
          label: 'Draft Forms',
          icon: EditIcon,
          path: '/forms/drafts',
          permission: 'forms',
        },
        {
          id: 'published-forms',
          label: 'Published Forms',
          icon: PublishIcon,
          path: '/forms/published',
          permission: 'forms',
        },
        {
          id: 'form-runtime',
          label: 'Form Runtime',
          icon: RuntimeIcon,
          path: '/form-runtime',
          permission: 'forms',
        },
        {
          id: 'form-analytics',
          label: 'Form Analytics',
          icon: AnalyticsIcon,
          path: '/forms/analytics',
          permission: 'forms',
        },
      ],
    },
    {
      id: 'ai-agents',
      label: 'AI Agents',
      icon: AgentIcon,
      type: 'expandable',
      permission: 'agents',
      children: [
        {
          id: 'agent-manager',
          label: 'Agent Manager',
          icon: SettingsIcon,
          path: '/agent-manager',
          permission: 'agents',
        },
        {
          id: 'agent-gallery',
          label: 'Agent Gallery',
          icon: StoreIcon,
          path: '/agents',
          permission: 'agents',
        },
        {
          id: 'create-agent',
          label: 'Create Agent',
          icon: AddIcon,
          path: '/agents/create',
          permission: 'agents',
        },
        {
          id: 'agent-groups',
          label: 'Agent Groups',
          icon: GroupIcon,
          path: '/agent-groups',
          permission: 'agents',
        },
        {
          id: 'rag-dashboard',
          label: 'RAG Dashboard',
          icon: DataIcon,
          path: '/rag-dashboard',
          permission: 'rag-dashboard',
        },
      ],
    },
    {
      id: 'automation',
      label: 'Automation & Workflows',
      icon: FlowIcon,
      type: 'expandable',
      permission: 'agents', // Requires agent permission
      children: automationWorkflowChildren,
    },
    {
      id: 'data-analytics',
      label: 'Data & Analytics',
      icon: AnalyticsIcon,
      type: 'expandable',
      permission: 'analytics',
      children: [
        {
          id: 'analytics',
          label: 'Analytics',
          icon: AnalyticsIcon,
          path: '/analytics',
          permission: 'analytics',
        },
        {
          id: 'data-sources',
          label: 'Data Sources',
          icon: DataIcon,
          path: '/data-sources',
          permission: 'data-sources',
        },
      ],
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: ExtensionIcon,
      type: 'expandable',
      permission: 'integrations.view',
      children: [
        {
          label: 'API Endpoints', path: '/api-endpoints', icon: ApiIcon, permission: 'integrations.view',
        },
        {
          label: 'Webhooks', path: '/webhooks', icon: CloudIcon, permission: 'integrations.manage',
        },
        {
          label: 'Third-party Apps', path: '/integrations', icon: ExtensionIcon, permission: 'integrations.configure',
        },
      ],
    },
    {
      id: 'mcp',
      label: 'MCP Server',
      icon: CodeIcon,
      type: 'expandable',
      permission: 'mcp.view',
      children: [
        {
          label: 'MCP Server Control', path: '/mcp-dashboard', icon: CodeIcon, permission: 'mcp.server',
        },
        {
          label: 'Flow Management', path: '/mcp-dashboard/system', icon: FlowIcon, permission: 'mcp.manage',
        },
        {
          label: 'Flow Templates', path: '/mcp-templates', icon: TemplateIcon, permission: 'mcp.view',
        },
        {
          label: 'AI Model Management', path: '/mcp-dashboard/ai', icon: AgentIcon, permission: 'mcp.manage',
        },
        {
          label: 'AI Analytics', path: '/mcp-analytics', icon: AnalyticsIcon, permission: 'mcp.analytics',
        },
        {
          label: 'System Monitoring', path: '/mcp-dashboard/monitoring', icon: MonitorIcon, permission: 'mcp.monitor',
        },
        {
          label: 'MCP Billing', path: '/mcp-dashboard/billing', icon: StoreIcon, permission: 'mcp.manage',
        },
        {
          label: 'MCP Tools', path: '/mcp-tools', icon: BuildIcon, permission: 'mcp.tools',
        },
        {
          label: 'Flow Search', path: '/mcp-search', icon: SearchIcon, permission: 'mcp.view',
        },
        {
          label: 'Flow Statistics', path: '/mcp-statistics', icon: AnalyticsIcon, permission: 'mcp.analytics',
        },
        {
          label: 'MCP Settings', path: '/mcp-dashboard/settings', icon: SettingsIcon, permission: 'mcp.manage',
        },
      ],
    },
    {
      id: 'system',
      label: 'System',
      icon: SettingsIcon,
      type: 'expandable',
      permission: 'settings',
      children: [
        {
          id: 'settings',
          label: 'Settings',
          icon: SettingsIcon,
          path: '/settings',
          permission: 'settings',
        },
        {
          id: 'security',
          label: 'Security',
          icon: SecurityIcon,
          path: '/security',
          permission: 'settings',
        },
        {
          id: 'performance',
          label: 'Performance',
          icon: PerformanceIcon,
          path: '/performance',
          permission: 'performance',
        },
        /* {
          id: 'user-management',
          label: 'User Management',
          icon: PeopleIcon,
          path: '/user-management',
          permission: 'settings', // Super admin only
        }, */
        {
          id: 'tenant-management',
          label: 'Tenant Management',
          icon: BusinessIcon,
          path: '/tenants',
          type: 'single',
          permission: 'settings',
          description: 'Manage existing tenants and their settings',
        },
        {
          id: 'tenant-approval',
          label: 'Tenant Approval',
          icon: CheckCircleIcon,
          path: '/tenant-approval',
          type: 'single',
          permission: 'settings',
          description: 'Review and approve new tenant applications',
          badge: pendingTenantsCount > 0 ? pendingTenantsCount : null,
        },
      ],
    },
    {
      id: 'help-support',
      label: 'Help & Support',
      icon: HelpIcon,
      type: 'expandable',
      permission: 'help',
      children: [
        {
          id: 'general-help',
          label: 'General Help',
          icon: HelpIcon,
          path: '/help',
          permission: 'help',
        },
        {
          id: 'agentic-workflow-help',
          label: 'Workflow Help',
          icon: FlowIcon,
          path: '/agentic-workflow-help',
          permission: 'help',
        },
        {
          id: 'mcp-help',
          label: 'MCP Help',
          icon: CodeIcon,
          path: '/mcp-help',
          permission: 'help',
        },
        ...(isInterviewflow
          ? [{
            id: 'studio-help',
            label: 'Studio Help',
            icon: MenuBookIcon,
            path: '/studio/help/overview',
            permission: 'help',
          }]
          : []),
      ],
    },
  ];

  // Navigation items with proper structure
  const navItems = [
    {
      label: 'Chat',
      path: '/home',
      icon: HomeIcon,
      type: 'item',
    },
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: DashboardIcon,
      type: 'item',
    },
    {
      id: 'workspace',
      label: 'Workspace',
      type: 'group',
      children: [
        { label: 'Form Builder', path: '/formBuilder', icon: FormIcon },
        { label: 'Form Runtime', path: '/form-runtime', icon: RuntimeIcon },
        { label: 'Agent Flow Builder', path: '/agentFlowBuilder', icon: FlowIcon },
        { label: 'App Builder', path: '/dashboard-builder', icon: GridIcon },
        { label: 'Apps', path: '/apps', icon: AppsIcon },
        { label: 'Agent Manager', path: '/agent-manager', icon: BuildIcon },
        { label: 'Analytics', path: '/analytics', icon: AnalyticsIcon },
        { label: 'Data Sources', path: '/data-sources', icon: DataIcon },
      ],
    },
    {
      id: 'agents',
      label: 'AI Agents',
      type: 'group',
      children: [
        { label: 'Agent Gallery', path: '/agents', icon: ExtensionIcon },
        { label: 'Create Agent', path: '/agents/create', icon: AddIcon },
        { label: 'Agent Groups', path: '/agent-groups', icon: TeamIcon },
        { label: 'Agent Templates', path: '/agent-templates', icon: CodeIcon },
        { label: 'Agent Marketplace', path: '/marketplace', icon: PublicIcon },
      ],
    },
    {
      id: 'integrations',
      label: 'Integrations',
      type: 'group',
      children: [
        { label: 'API Endpoints', path: '/api-endpoints', icon: ApiIcon },
        { label: 'Webhooks', path: '/webhooks', icon: CloudIcon },
        { label: 'Third-party Apps', path: '/integrations', icon: ExtensionIcon },
      ],
    },
    {
      id: 'system',
      label: 'System',
      type: 'group',
      children: [
        { label: 'Settings', path: '/settings', icon: SettingsIcon },
        { label: 'Security', path: '/security', icon: SecurityIcon },
        { label: 'Performance', path: '/performance', icon: PerformanceIcon },
        { label: 'Help & Support', path: '/help', icon: HelpIcon },
        { label: 'Agentic Workflow Help', path: '/agentic-workflow-help', icon: FlowIcon },
      ],
    },
  ];

  // InterviewFlow: Chat, Dashboard, Forms, Automation, MCP, System, Help
  const NAV_IDS_INTERVIEWFLOW = ['dashboard', 'chat', 'forms', 'automation', 'crm', 'mcp', 'system', 'help-support'];
  const allNavigationItems = isInterviewflow
    ? navigationItems.filter((item) => NAV_IDS_INTERVIEWFLOW.includes(item.id))
    : navigationItems;

  // Filter navigation items based on user permissions
  const filterNavigationItems = (items) => items.filter((item) => {
    // Fix: Always allow super_admin to see all items
    if (userRole === 'super_admin') {
      return true;
    }
    // Check if user has permission for this item
    if (item.permission && !hasPermission(item.permission)) {
      return false;
    }
    // If item has children, filter them too
    if (item.children) {
      item.children = filterNavigationItems(item.children);
      // Only show parent if it has accessible children or if parent itself is accessible
      return item.children.length > 0;
    }
    return true;
  });

  // Get filtered navigation items (use brand-filtered list for InterviewFlow)
  const filteredNavigationItems = filterNavigationItems([...allNavigationItems]);

  const navDrawerHasLabels = (open && !isMobile) || isMobile;

  const studioRecentsNav = useMemo(() => {
    if (!isInterviewflow) return [];
    const list = listStudioRecents().filter((x) => x?.href);
    return [...list].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return Number(b.t || 0) - Number(a.t || 0);
    }).slice(0, 8);
  }, [isInterviewflow, recentsRefreshKey, location.pathname]);

  const commandPaletteEntries = useMemo(() => {
    if (!isInterviewflow) return [];
    const byPath = new Map();
    const add = (label, path, section) => {
      if (!path || byPath.has(path)) return;
      byPath.set(path, { label, path, section });
    };
    flattenFilteredNavigationForPalette(filteredNavigationItems).forEach((r) => {
      add(r.label, r.path, r.section);
    });
    listStudioRecents().filter((x) => x?.href).forEach((r) => {
      add(r.pinned ? `Pin: ${r.name}` : r.name, r.href, 'Recents');
    });
    add('Studio home', '/studio', 'Quick');
    add('Flows', '/studio/flows', 'Quick');
    add('Apps', '/studio/apps', 'Quick');
    add('Starters', '/studio/templates', 'Quick');
    add('AI assistants', '/studio/connect', 'Quick');
    add('Forms (design)', '/studio/forms', 'Quick');
    if (userRole === 'super_admin') {
      add('Tenant management', '/tenants', 'Admin');
      add('Tenant approval', '/tenant-approval', 'Admin');
    }
    return Array.from(byPath.values());
  }, [isInterviewflow, filteredNavigationItems, recentsRefreshKey, userRole, location.pathname]);

  const interviewflowContextChips = useMemo(() => {
    if (!isInterviewflow) return [];
    const chips = [];
    const p = location.pathname;
    if (/^\/studio\/flows\/[^/]+$/.test(p)) chips.push({ label: 'All flows', path: '/studio/flows' });
    if (/^\/studio\/apps\/[^/]+$/.test(p)) chips.push({ label: 'All apps', path: '/studio/apps' });
    if (/^\/studio\/forms\/design/.test(p) || p === '/formBuilder') {
      chips.push({ label: 'Forms hub', path: '/studio/forms' });
    }
    if (userRole === 'super_admin' && hasPermission('settings')) {
      chips.push({ label: 'Tenants', path: '/tenants' });
    }
    return chips.slice(0, 5);
  }, [isInterviewflow, location.pathname, userRole, hasPermission]);

  // Add missing renderNavigationItem function with permission checks
  const renderNavigationItem = (item) => {
    // Double-check permissions (in case filtering missed something)
    if (item.permission && !hasPermission(item.permission)) {
      return null;
    }

    if (item.type === 'single') {
      return (
        <ListItem key={item.id} disablePadding>
          <ListItemButton
            onClick={() => {
              navigate(item.path);
              // Only close mobile drawer, never collapse desktop nav
              if (isMobile) {
                handleDrawerToggle();
              }
            }}
            selected={navPathSelected(location.pathname, item.path)}
            sx={{
              minHeight: 46,
              justifyContent: navDrawerHasLabels ? 'initial' : 'center',
              px: 2.5,
              mx: isInterviewflow ? 1 : 0,
              borderRadius: isInterviewflow ? 2 : 0,
              ...(isInterviewflow
                ? {
                  '&.Mui-selected': {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
                    '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.2) },
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                  },
                }
                : {
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                    '&:hover': { backgroundColor: 'action.selected' },
                  },
                }),
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: navDrawerHasLabels ? 3 : 'auto',
                justifyContent: 'center',
                ...(isInterviewflow && { '& svg': { fontSize: '1.25rem' } }),
              }}
            >
              {item.icon && <item.icon />}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              sx={{
                opacity: navDrawerHasLabels ? 1 : 0,
                '& .MuiTypography-root': {
                  fontSize: isMobile ? '0.9rem' : isInterviewflow ? '0.875rem' : '1rem',
                  fontWeight: isMobile || isInterviewflow ? 500 : 400,
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      );
    }

    if (item.type === 'expandable') {
      const isExpanded = expandedItems[item.id];
      const hasSelectedChild = item.children?.some((child) => navPathSelected(location.pathname, child.path));

      // Filter children based on permissions
      const accessibleChildren = item.children?.filter((child) => !child.permission || hasPermission(child.permission)) || [];

      // Don't render if no accessible children
      if (accessibleChildren.length === 0) {
        return null;
      }

      return (
        <React.Fragment key={item.id}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleExpandClick(item.id)}
              selected={hasSelectedChild}
              sx={{
                minHeight: 46,
                justifyContent: navDrawerHasLabels ? 'initial' : 'center',
                px: 2.5,
                mx: isInterviewflow ? 1 : 0,
                borderRadius: isInterviewflow ? 2 : 0,
                ...(isInterviewflow
                  ? {
                    '&.Mui-selected': {
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                      '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.12) },
                      '& .MuiListItemIcon-root': { color: 'primary.main' },
                    },
                  }
                  : {
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                      '&:hover': { backgroundColor: 'action.selected' },
                    },
                  }),
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: navDrawerHasLabels ? 3 : 'auto',
                  justifyContent: 'center',
                  ...(isInterviewflow && { '& svg': { fontSize: '1.25rem' } }),
                }}
              >
                {item.icon && <item.icon />}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                sx={{
                  opacity: navDrawerHasLabels ? 1 : 0,
                  '& .MuiTypography-root': {
                    fontSize: isMobile ? '0.9rem' : isInterviewflow ? '0.8125rem' : '1rem',
                    fontWeight: isInterviewflow ? 600 : isMobile ? 500 : 400,
                    letterSpacing: isInterviewflow ? 0.02 : undefined,
                    textTransform: isInterviewflow ? 'uppercase' : undefined,
                    color: isInterviewflow ? 'text.secondary' : undefined,
                  },
                }}
              />
              {navDrawerHasLabels && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>

          {navDrawerHasLabels && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={isInterviewflow ? { pl: 0.5, pr: 0.5 } : undefined}>
                {accessibleChildren.map((child) => (
                  <ListItemButton
                    key={child.id}
                    onClick={() => {
                      navigate(child.path);
                      if (isMobile) {
                        handleDrawerToggle();
                      }
                      // Do NOT collapse parent, do NOT call handleExpandClick here
                      // Optionally, keep parent expanded:
                      setExpandedItems((prev) => ({ ...prev, [item.id]: true }));
                    }}
                    selected={navPathSelected(location.pathname, child.path)}
                    sx={{
                      pl: isInterviewflow ? 2.5 : 4,
                      pr: isInterviewflow ? 1.5 : undefined,
                      mx: isInterviewflow ? 1 : 0,
                      my: isInterviewflow ? 0.25 : 0,
                      minHeight: 40,
                      borderRadius: isInterviewflow ? 2 : 0,
                      ...(isInterviewflow
                        ? {
                          '&.Mui-selected': {
                            bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
                            '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.2) },
                            '& .MuiListItemIcon-root': { color: 'primary.main' },
                          },
                        }
                        : {
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': { backgroundColor: 'primary.dark' },
                            '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                          },
                        }),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 2,
                        justifyContent: 'center',
                        ...(isInterviewflow && { '& svg': { fontSize: '1.125rem' } }),
                      }}
                    >
                      {child.icon && <child.icon />}
                    </ListItemIcon>
                    <ListItemText
                      primary={child.label}
                      primaryTypographyProps={{
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        fontWeight: 400,
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    }

    if (item.type === 'group') {
      // Filter children based on permissions
      const accessibleChildren = item.children?.filter((child) => !child.permission || hasPermission(child.permission)) || [];

      // Don't render if no accessible children
      if (accessibleChildren.length === 0) {
        return null;
      }

      const hasSelectedChild = accessibleChildren.some((child) => navPathSelected(location.pathname, child.path));

      return (
        <React.Fragment key={item.id}>
          {isInterviewflow ? (
            <ListSubheader
              component="div"
              disableSticky
              sx={{
                bgcolor: 'background.paper',
                color: 'text.secondary',
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                lineHeight: 2.25,
                mt: 0.5,
                py: 0,
                opacity: navDrawerHasLabels ? 1 : 0,
              }}
            >
              {item.label}
            </ListSubheader>
          ) : (
            <ListItem disablePadding>
              <ListItemButton
                selected={hasSelectedChild}
                sx={{
                  minHeight: 48,
                  justifyContent: navDrawerHasLabels ? 'initial' : 'center',
                  px: 2.5,
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                    '&:hover': { backgroundColor: 'action.selected' },
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    opacity: navDrawerHasLabels ? 1 : 0,
                    '& .MuiTypography-root': {
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      fontWeight: 'bold',
                      color: 'primary.main',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          )}
          {accessibleChildren.map((child) => (
            <ListItem key={child.id || child.path} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(child.path);
                  if (isMobile) {
                    handleDrawerToggle();
                  }
                }}
                selected={navPathSelected(location.pathname, child.path)}
                sx={{
                  minHeight: 40,
                  justifyContent: navDrawerHasLabels ? 'initial' : 'center',
                  px: 2.5,
                  pl: isInterviewflow ? 2 : 4,
                  mx: isInterviewflow ? 1 : 0,
                  my: isInterviewflow ? 0.25 : 0,
                  borderRadius: isInterviewflow ? 2 : 0,
                  ...(isInterviewflow
                    ? {
                      '&.Mui-selected': {
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
                        '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.2) },
                        '& .MuiListItemIcon-root': { color: 'primary.main' },
                      },
                    }
                    : {
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        '&:hover': { backgroundColor: 'action.selected' },
                      },
                    }),
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: navDrawerHasLabels ? 2 : 'auto',
                    justifyContent: 'center',
                    ...(isInterviewflow && { '& svg': { fontSize: '1.125rem' } }),
                  }}
                >
                  {child.icon && <child.icon />}
                </ListItemIcon>
                <ListItemText
                  primary={child.label}
                  sx={{
                    opacity: navDrawerHasLabels ? 1 : 0,
                    '& .MuiTypography-root': {
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                      fontWeight: isInterviewflow ? 500 : 400,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </React.Fragment>
      );
    }

    return null;
  };

  const q = commandQuery.trim().toLowerCase();
  const filteredCommandPalette = !q
    ? commandPaletteEntries.slice(0, 40)
    : commandPaletteEntries
      .filter((e) => e.label.toLowerCase().includes(q) || e.path.toLowerCase().includes(q))
      .slice(0, 40);

  const runCommandNavigate = (path) => {
    if (!path) return;
    navigate(path);
    setCommandOpen(false);
    setCommandQuery('');
    if (isMobile) handleDrawerToggle();
  };

  // Drawer content with better mobile handling
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : (isNavCollapsed ? 'center' : 'space-between'),
          minHeight: 64,
        }}
      >
        {(!isNavCollapsed || isMobile) && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              fontSize: isMobile ? '1.1rem' : '1.25rem',
            }}
          >
            AI Studio
          </Typography>
        )}
        {!isMobile && (
          <IconButton onClick={handleNavToggle} size="small">
            {isNavCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Box>

      {isInterviewflow && navDrawerHasLabels && (
        <Box sx={{
          px: 1.5, pt: 1, pb: 1.5, borderBottom: 1, borderColor: 'divider',
        }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>
            Quick actions
          </Typography>
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1,
          }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                navigate('/studio/flows');
                if (isMobile) handleDrawerToggle();
              }}
            >
              Flows
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                navigate('/studio/apps');
                if (isMobile) handleDrawerToggle();
              }}
            >
              Apps
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<SearchIcon />}
              onClick={() => setCommandOpen(true)}
            >
              Go to…
            </Button>
          </Box>
          {interviewflowContextChips.length > 0 && (
            <Box sx={{
              display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1,
            }}
            >
              {interviewflowContextChips.map((c) => (
                <Chip
                  key={`${c.path}-${c.label}`}
                  label={c.label}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    navigate(c.path);
                    if (isMobile) handleDrawerToggle();
                  }}
                />
              ))}
            </Box>
          )}
          {studioRecentsNav.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                Recents
              </Typography>
              <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto' }}>
                {studioRecentsNav.map((r) => (
                  <ListItem key={`${r.kind}-${r.id}`} disablePadding sx={{ position: 'relative', mb: 0.25 }}>
                    <ListItemButton
                      dense
                      onClick={() => {
                        if (r.href) navigate(r.href);
                        if (isMobile) handleDrawerToggle();
                      }}
                      sx={{
                        borderRadius: 1,
                        pr: 5,
                        alignItems: 'flex-start',
                      }}
                    >
                      <ListItemText
                        primary={r.name || r.id}
                        secondary={r.kind === 'flow' ? 'Flow' : r.kind === 'app' ? 'App' : r.kind}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                    <IconButton
                      aria-label={r.pinned ? 'Unpin recent' : 'Pin recent'}
                      size="small"
                      sx={{
                        position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleStudioRecentPin(r.kind, r.id);
                        bumpRecents();
                      }}
                    >
                      {r.pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      )}

      {/* Navigation Items */}
      <List sx={{ flex: 1, py: 1, ...(isInterviewflow ? { pt: 0.5 } : {}) }}>
        {filteredNavigationItems.map((item) => (
          <React.Fragment key={item.id}>
            {renderNavigationItem(item)}
          </React.Fragment>
        ))}
      </List>

      {/* User Section */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        {/* Role Indicator */}
        {(!isNavCollapsed || isMobile) && (
          <Box sx={{ mb: 2, px: 1 }}>
            <RoleIndicator />
          </Box>
        )}

        <ListItemButton
          onClick={handleUserMenuOpen}
          sx={{
            borderRadius: 1,
            justifyContent: (isNavCollapsed && !isMobile) ? 'center' : 'flex-start',
            px: (isNavCollapsed && !isMobile) ? 1 : 2,
          }}
        >
          <ListItemIcon sx={{
            color: 'inherit',
            minWidth: (isNavCollapsed && !isMobile) ? 'auto' : 40,
          }}
          >
            <AccountCircleIcon />
          </ListItemIcon>
          {(!isNavCollapsed || isMobile) && (
            <ListItemText
              primary="Account"
              sx={{ ml: 1 }}
            />
          )}
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        '--drawer-width': `${desktopDrawerWidth}px`, // CSS custom property for drawer width
      }}
    >
      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: mobileDrawerWidth, // Use wider mobile width
            bgcolor: 'background.paper',
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Navigation Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: desktopDrawerWidth, // Use desktop width
            bgcolor: 'background.paper',
            borderRight: `1px solid ${theme.palette.divider}`,
            position: 'relative',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {isInterviewflow && (
        <Dialog
          open={commandOpen}
          onClose={() => {
            setCommandOpen(false);
            setCommandQuery('');
          }}
          fullWidth
          maxWidth="sm"
          aria-labelledby="nav-go-to-title"
        >
          <DialogTitle id="nav-go-to-title">Go to</DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Search pages and recents…"
              value={commandQuery}
              onChange={(e) => setCommandQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Ctrl+K or ⌘K to toggle
            </Typography>
            <List dense sx={{ maxHeight: 360, overflow: 'auto', mt: 1 }}>
              {filteredCommandPalette.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No matches
                </Typography>
              ) : (
                filteredCommandPalette.map((e, idx) => (
                  <ListItemButton
                    key={`${e.path}-${idx}`}
                    onClick={() => runCommandNavigate(e.path)}
                  >
                    <ListItemText
                      primary={e.label}
                      secondary={e.path}
                      primaryTypographyProps={{ noWrap: true }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                    <Chip label={e.section} size="small" variant="outlined" sx={{ ml: 1, flexShrink: 0 }} />
                  </ListItemButton>
                ))
              )}
            </List>
          </DialogContent>
        </Dialog>
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          width: {
            xs: '100%', // Full width on mobile
            md: `calc(100% - ${desktopDrawerWidth}px)`,
          },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Top App Bar — InterviewFlow: gradient accent; Baiglabs: standard */}
        <AppBar
          position="sticky"
          elevation={getBrand() === 'interviewflow' ? 0 : 1}
          sx={{
            bgcolor: 'background.paper',
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: 'text.primary',
            ...(getBrand() === 'interviewflow' && {
              boxShadow: '0 1px 12px rgba(25, 118, 210, 0.06)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #ffb74d 100%)',
                zIndex: 1,
              },
            }),
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
            {/* Mobile Menu Button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo */}
            <Box sx={{ mr: 2, display: { xs: 'none', sm: 'flex' } }}>
              <BrandLogo
                size="small"
                orientation="horizontal"
                showTagline={false}
                iconSize={20}
              />
            </Box>

            {/* InterviewFlow: Top nav links (no Dashboard, Apps, Help per request) */}
            {getBrand() === 'interviewflow' && !isMobile && (
              <Box sx={{
                display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', mr: 2,
              }}
              >
                <Button
                  size="small"
                  onClick={() => navigate('/home')}
                  sx={{
                    color: location.pathname === '/home' ? '#1976d2' : 'text.primary', fontWeight: location.pathname === '/home' ? 700 : 600, textTransform: 'none', minWidth: 'auto', px: 1.5,
                  }}
                >Chat
                </Button>
                <Button
                  size="small"
                  onClick={() => navigate('/studio')}
                  sx={{
                    color: location.pathname.startsWith('/studio') ? '#1976d2' : 'text.primary', fontWeight: location.pathname.startsWith('/studio') ? 700 : 600, textTransform: 'none', minWidth: 'auto', px: 1.5,
                  }}
                >Studio
                </Button>
                <Button
                  size="small"
                  onClick={() => navigate('/studio/forms')}
                  sx={{
                    color: (location.pathname.startsWith('/studio/forms') || location.pathname === '/formBuilder') ? '#1976d2' : 'text.primary', fontWeight: (location.pathname.startsWith('/studio/forms') || location.pathname === '/formBuilder') ? 700 : 600, textTransform: 'none', minWidth: 'auto', px: 1.5,
                  }}
                >Forms
                </Button>
                <Button
                  size="small"
                  onClick={() => navigate('/mcp-dashboard')}
                  sx={{
                    color: location.pathname.startsWith('/mcp-dashboard') ? '#1976d2' : 'text.primary', fontWeight: location.pathname.startsWith('/mcp-dashboard') ? 700 : 600, textTransform: 'none', minWidth: 'auto', px: 1.5,
                  }}
                >MCP
                </Button>
              </Box>
            )}

            {/* Page Title - Responsive (hidden for /home and /dashboard) */}
            {location.pathname !== '/home' && location.pathname !== '/dashboard' && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mr: { xs: 1, sm: 3 },
                minWidth: 0, // Allow shrinking
                flex: { xs: '0 1 auto', sm: 'none' },
              }}
              >
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {/* Shorter titles for mobile */}
                  {location.pathname === '/dashboard' && (isMobile ? 'Dashboard' : 'Dashboard')}
                  {location.pathname === '/formBuilder' && (isMobile ? 'Forms' : 'Form Builder')}
                  {location.pathname.startsWith('/studio/forms') && (isMobile ? 'Forms' : 'Studio Forms')}
                  {location.pathname === '/form-runtime' && (isMobile ? 'Runtime' : 'Form Runtime')}
                  {location.pathname === '/agentFlowBuilder' && (isMobile ? 'Flow' : 'Agent Flow Builder')}
                  {location.pathname === '/dashboard-builder' && (isMobile ? 'Apps' : 'App Builder')}
                  {location.pathname === '/studio' && 'Studio'}
                  {location.pathname === '/studio/templates' && (isMobile ? 'Starters' : 'Starter templates')}
                  {location.pathname === '/studio/connect' && 'AI assistants'}
                  {location.pathname.startsWith('/studio/help') && (() => {
                    const sectionId = location.pathname.split('/')[3];
                    const section = sectionId ? STUDIO_HELP_SECTION_BY_ID[sectionId] : null;
                    return section ? section.title : (isMobile ? 'Help' : 'Studio Help');
                  })()}
                  {location.pathname.startsWith('/studio/flows') && (isMobile ? 'Flows' : 'V2 Flows')}
                  {location.pathname.startsWith('/studio/apps') && (isMobile ? 'Apps' : 'V2 Apps')}
                  {location.pathname === '/apps' && 'Apps'}
                  {location.pathname.startsWith('/dashboard-builder/run/') && (isMobile ? 'App' : 'App')}
                  {location.pathname === '/agent-manager' && (isMobile ? 'Agents' : 'Agent Manager')}
                  {location.pathname === '/agents' && (isMobile ? 'Gallery' : 'Agent Gallery')}
                  {location.pathname === '/agents/create' && (isMobile ? 'Create' : 'Create Agent')}
                  {location.pathname === '/agent-groups' && (isMobile ? 'Groups' : 'Agent Groups')}
                  {location.pathname === '/settings' && 'Settings'}
                  {location.pathname === '/security' && 'Security'}
                  {location.pathname === '/performance' && (isMobile ? 'Perf' : 'Performance')}
                  {location.pathname === '/help' && 'Help'}
                </Typography>
              </Box>
            )}

            {/* Global Search - Responsive */}
            <Box sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: location.pathname === '/home' ? 'flex-end' : 'center',
              px: { xs: 1, sm: 2 },
              minWidth: 0,
            }}
            >
              {location.pathname !== '/home' && (
                <Box sx={{
                  width: '100%',
                  maxWidth: { xs: 250, sm: 400, md: 500 },
                  minWidth: { xs: 120, sm: 96, lg: 250 },
                }}
                >
                  <GlobalSearch />
                </Box>
              )}
            </Box>

            {/* Right Side Actions - Responsive */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              minWidth: 0,
              flexShrink: { xs: 1, sm: 0 },
            }}
            >
              {/* Global Search - Mini version for chat page */}
              {location.pathname === '/home' && (
                <Box sx={{
                  width: { xs: 150, sm: 250, md: 300 },
                  minWidth: 100,
                }}
                >
                  <GlobalSearch />
                </Box>
              )}

              {/* Theme Toggle */}
              <Tooltip title={darkMode ? 'Light Mode' : 'Dark Mode'}>
                <IconButton
                  onClick={onToggleTheme}
                  color="inherit"
                  size={isMobile ? 'small' : 'medium'}
                >
                  {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>

              {/* Fullscreen Toggle */}
              <FullscreenButton
                color="inherit"
                size={isMobile ? 'small' : 'medium'}
              />

              {/* Notifications - Hide on very small screens */}
              <Tooltip title="Notifications">
                <IconButton
                  color="inherit"
                  size={isMobile ? 'small' : 'medium'}
                  onClick={() => setShowNotifications(true)}
                  sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  <Badge badgeContent={notifications.length} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* User Menu */}
              <Tooltip title="Account">
                <IconButton
                  onClick={handleMenuClick}
                  color="inherit"
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ ml: { xs: 0.5, sm: 1 } }}
                >
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>

        {/* User Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
            <ListItemIcon>
              <AccountIcon />
            </ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>

        {/* User Menu from userMenuAnchor */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => { handleUserMenuClose(); navigate('/profile'); }}>
            <ListItemIcon>
              <AccountIcon />
            </ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => { handleUserMenuClose(); navigate('/settings'); }}>
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { handleUserMenuClose(); onLogout?.(); }}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>

        {/* Notifications Drawer/Page */}
        <Drawer
          anchor="right"
          open={showNotifications}
          onClose={() => setShowNotifications(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 400,
              maxWidth: '90vw',
              boxSizing: 'border-box',
              bgcolor: 'background.paper',
              borderLeft: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <NotificationsPage
            notifications={notifications}
            onClose={() => setShowNotifications(false)}
          />
        </Drawer>

        {/* Page Content */}
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflow: 'auto',
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export default AppNavigation;
